import { Button, Center, Divider, Group, NumberInput, Select, Space, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AIModelType,
  bindBotDatasets,
  checkIsUrl,
  createRobotDatasets,
  createWebConnector,
  useDataSourcesModal,
} from '@mygpt/app';
import { showNotification } from '@mantine/notifications';

import { ModelSelect } from '@/components/Create_V2/ModelSelect';
import { useCreateStore } from '@/store/create';
import { useGPTStore } from '@/store';
import { useWebConnectorStore } from '@/store/webConnector';
import { createWebConnectorV2, previewUrlPatterns, PreviewResponse } from '@mygpt/app/apis/services';
import { autoCompleteUrlScheme } from '@/utils/helper';

import { UrlPatternInput } from './UrlPatternInput';
import { ScheduleSelector, ScheduleValue } from './ScheduleSelector';
import { dedupeByCanonical, isValidUrl } from './csvUrlParser';

// 保留导出，供其他文件引用（V1 兼容）
export const websiteTypeEum = {
  website: 'website',
  single: 'single',
  sitemap: 'sitemap',
  gitbook: 'gitbook',
};

const Website = () => {
  const { t } = useTranslation();

  const { pageType, datasetId, setDatasetId, embeddingModelName, setPageType } = useCreateStore();

  const { assetScope } = useGPTStore();
  const isCloudMode = import.meta.env.VITE_MODE !== 'local';
  const { onClose } = useDataSourcesModal();
  const { fetchConnectors } = useWebConnectorStore();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCustomPages, setIsCustomPages] = useState(false);
  const [customPages, setCustomPages] = useState<number>(400);

  // Preview 状态
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState<PreviewResponse | null>(null);

  // Schedule 状态
  const [schedule, setSchedule] = useState<ScheduleValue>({
    enabled: false,
    frequency: 'monthly',
    dayOfWeek: 1,
    dayOfMonth: 1,
    hour: 9,
    minute: 0,
  });

  // 自定义页面数处理函数
  const handleCustomPagesChange = (value: string | number) => {
    if (value === '' || value === undefined) return;
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    if (!isNaN(numValue)) {
      setCustomPages(Math.max(1, Math.min(10000, numValue)));
    }
  };

  const form = useForm({
    initialValues: {
      name: '',
      urlPatterns: [''],
      excludePatterns: [''],
      maxPages: '400',
      isSpa: '0',
      discoveryMode: 'auto',
    },
    validate: {
      name: (value) => (value.trim() ? null : t('create_v2.website.name_required')),
      urlPatterns: (value) => {
        const nonEmpty = value.filter((u) => u.trim());
        return nonEmpty.length > 0 ? null : t('create_v2.website.target_url_required');
      },
    },
  });

  const submit = async () => {
    const validation = form.validate();
    if (validation.hasErrors) return;

    setIsSubmitting(true);

    try {
      const name = form.values.name.trim();
      const { unique: urlPatterns, removed: dupRemoved } = dedupeByCanonical(
        form.values.urlPatterns.filter((u) => u.trim()).map(autoCompleteUrlScheme)
      );
      const excludePatterns = form.values.excludePatterns.filter((u) => u.trim()).map(autoCompleteUrlScheme);
      const maxPages = isCustomPages ? customPages : Number(form.values.maxPages);
      const isSpa = Boolean(+form.values.isSpa);

      let targetDatasetId = datasetId;

      // 如果是创建模式，先创建数据集
      if (pageType === 'create') {
        const res = await createRobotDatasets({
          name,
          aiModel: AIModelType.WEBSITE,
          url: urlPatterns[0],
          embeddingModelName,
          ...(isCloudMode && assetScope && { companyId: assetScope }),
        });

        targetDatasetId = res.data.id;
        setDatasetId(targetDatasetId);
        setPageType('modify');
      }

      // 构建 V2 config
      await createWebConnectorV2(targetDatasetId, {
        name,
        type: 'web_connector',
        config: {
          enable_sync: schedule.enabled,
          schedule: schedule.enabled
            ? {
                frequency: schedule.frequency,
                day_of_week: schedule.frequency === 'weekly' ? schedule.dayOfWeek : undefined,
                day_of_month: schedule.frequency === 'monthly' ? schedule.dayOfMonth : undefined,
                hour: schedule.hour,
                minute: schedule.minute,
                timezone: 'Asia/Tokyo',
              }
            : undefined,
          web_connector: {
            url_patterns: urlPatterns,
            exclude_patterns: excludePatterns,
            max_pages: maxPages,
            is_spa: isSpa,
            discovery_mode: form.values.discoveryMode as 'auto' | 'sitemap' | 'crawler',
          },
        },
      });

      showNotification({
        message: t('adminConsole.tip.create_success'),
        color: 'green',
      });

      if (dupRemoved > 0) {
        showNotification({
          message: t('create_v2.website.duplicates_removed', { count: dupRemoved }),
          color: 'orange',
        });
      }

      // 关闭创建模态框
      onClose();

      // 检查当前是否已经在 web-connector 列表页面
      const currentPath = location.pathname;
      const isOnWebConnectorPage =
        currentPath.includes('/data-source/web-connector') &&
        !currentPath.match(/\/data-source\/web-connector\/[^/]+$/);

      if (isOnWebConnectorPage) {
        if (targetDatasetId) {
          fetchConnectors({
            datasetId: targetDatasetId,
            page: 1,
            size: 10,
          });
        }
      } else {
        if (pageType === 'create') {
          const newBotId = queryParams.get('botId');
          bindBotDatasets({
            aiId: newBotId,
            datasetIds: [targetDatasetId],
            type: 'bind',
          }).then(() => {
            navigate(`/bot/${newBotId}/data-source`);
          });
        } else {
          const basePath = currentPath.replace(/\/data-source.*$/, '');
          navigate(`${basePath}/data-source/web-connector`);
        }
      }
    } catch (error: any) {
      showNotification({
        message: error?.response?.data?.detail || error.message || t('tip.create_error'),
        color: 'red',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePreview = async () => {
    if (!datasetId) return;
    const urlPatterns = form.values.urlPatterns.filter((u) => u.trim());
    if (urlPatterns.length === 0) return;

    setPreviewLoading(true);
    setPreviewResult(null);
    try {
      const maxPages = isCustomPages ? customPages : Number(form.values.maxPages);
      const res = await previewUrlPatterns(datasetId, {
        url_patterns: urlPatterns,
        exclude_patterns: form.values.excludePatterns.filter((u) => u.trim()),
        max_pages: maxPages,
        is_spa: Boolean(+form.values.isSpa),
        discovery_mode: form.values.discoveryMode as 'auto' | 'sitemap' | 'crawler',
      });
      setPreviewResult(res.data);
    } catch (error: any) {
      showNotification({
        message: error?.response?.data?.detail || error.message || 'Preview failed',
        color: 'red',
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const hasValidUrl = form.values.urlPatterns.some((u) => {
    const trimmed = u.trim();
    return trimmed !== '' && isValidUrl(autoCompleteUrlScheme(trimmed));
  });

  return (
    <section>
      <h3 className="gray.8 font-medium">{t('create_v2.website.label')}</h3>
      <Space h={10} />

      <form
        className="mt-2"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {/* 名前 */}
        <TextInput
          label={t('create_v2.integrations.task_management.name')}
          placeholder={t('create_v2.integrations.task_management.name_placeholder')}
          required
          mb="md"
          {...form.getInputProps('name')}
        />

        {/* 対象URL */}
        <UrlPatternInput
          label={t('create_v2.website.target_url')}
          value={form.values.urlPatterns}
          onChange={(v) => form.setFieldValue('urlPatterns', v)}
          minRows={1}
          variant="include"
          error={form.errors.urlPatterns as string}
        />

        <Space h="md" />

        {/* 除外URL */}
        <UrlPatternInput
          label={t('create_v2.website.exclude_url')}
          value={form.values.excludePatterns}
          onChange={(v) => form.setFieldValue('excludePatterns', v)}
          minRows={1}
          variant="exclude"
        />

        <Space h="md" />

        {/* 爬虫设置 */}
        <Group className="bg-violet-100 p-4 rounded-lg">
          <Text>{t('create_v2.website.crawler_setting.title')}</Text>
          <Divider color="violet.2" className="w-full" />
          <Select
            label={t('create_v2.website.crawler_setting.type_title')}
            description={t('create_v2.website.crawler_setting.type_desc')}
            className="w-full"
            value={form.values.isSpa}
            onChange={(v) => form.setFieldValue('isSpa', v || '0')}
            data={[
              { value: '0', label: t('create_v2.website.crawler_setting.type.raw') },
              { value: '1', label: t('create_v2.website.crawler_setting.type.headless') },
            ]}
            allowDeselect={false}
          />
          <Select
            label={t('create_v2.website.crawler_setting.discovery_mode')}
            description={t('create_v2.website.crawler_setting.discovery_mode_desc')}
            className="w-full"
            value={form.values.discoveryMode}
            onChange={(v) => form.setFieldValue('discoveryMode', v || 'auto')}
            data={[
              { value: 'auto', label: t('create_v2.website.crawler_setting.discovery.auto') },
              { value: 'sitemap', label: t('create_v2.website.crawler_setting.discovery.sitemap') },
              { value: 'crawler', label: t('create_v2.website.crawler_setting.discovery.crawler') },
            ]}
            allowDeselect={false}
          />
        </Group>

        {/* URL プレビュー（modify モードでのみ表示） */}
        {pageType === 'modify' && datasetId && (
          <div className="mt-2 mb-2">
            <Button variant="light" size="xs" onClick={handlePreview} loading={previewLoading} disabled={!hasValidUrl}>
              {t('create_v2.website.preview_btn')}
            </Button>
            {previewResult && (
              <div className="mt-2 p-3 bg-gray-50 rounded border" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {previewResult.patterns.map((p, i) => (
                  <div key={i} className="mb-2">
                    <Group justify="space-between" mb={2}>
                      <Text size="xs" c="gray.7" className="truncate" style={{ maxWidth: '70%' }}>
                        {p.pattern}
                      </Text>
                      <Text size="xs" fw={500}>
                        {p.matched_count} URLs
                      </Text>
                    </Group>
                    {p.sample_urls?.length > 0 && (
                      <div className="pl-2 border-l-2 border-gray-200">
                        {p.sample_urls.map((url, j) => (
                          <Text key={j} size="xs" c="dimmed" className="truncate" title={url}>
                            {url}
                          </Text>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <Divider my="xs" />
                <Group justify="space-between">
                  <Text size="xs" fw={600}>
                    {t('create_v2.website.preview_total')}
                  </Text>
                  <Text
                    size="xs"
                    fw={600}
                    c={
                      previewResult.total_urls > (isCustomPages ? customPages : Number(form.values.maxPages))
                        ? 'red'
                        : undefined
                    }
                  >
                    {previewResult.total_urls} URLs
                    {' / '}
                    {isCustomPages ? customPages : form.values.maxPages} {t('create_v2.website.preview_page_limit')}
                  </Text>
                </Group>
              </div>
            )}
          </div>
        )}

        <Space h="md" />

        {/* ページ数制限 */}
        <Group justify="space-between" className="relative" wrap="nowrap">
          <Text size="sm" fw={500}>
            {t('create_v2.website.page_limit')}
          </Text>
          <Group gap="xs" wrap="nowrap">
            <Select
              size="sm"
              value={isCustomPages ? 'custom' : form.values.maxPages}
              onChange={(value) => {
                if (value === 'custom') {
                  setIsCustomPages(true);
                } else {
                  setIsCustomPages(false);
                  form.setFieldValue('maxPages', value);
                }
              }}
              data={[
                { value: '400', label: t('create_v2.website.pages_num', { num: 400 }) },
                { value: '600', label: t('create_v2.website.pages_num', { num: 600 }) },
                { value: '1200', label: t('create_v2.website.pages_num', { num: 1200 }) },
                { value: '1800', label: t('create_v2.website.pages_num', { num: 1800 }) },
                { value: 'custom', label: t('create_v2.website.pages_custom') },
              ]}
              allowDeselect={false}
              style={{ minWidth: '120px' }}
            />
            {isCustomPages && (
              <NumberInput
                size="sm"
                min={1}
                max={10000}
                step={100}
                value={customPages}
                onChange={handleCustomPagesChange}
                onBlur={(e) => {
                  const inputValue = parseInt(e.currentTarget.value, 10);
                  if (isNaN(inputValue) || inputValue < 1) {
                    setCustomPages(1);
                  } else if (inputValue > 10000) {
                    setCustomPages(10000);
                  }
                }}
                style={{ width: '100px' }}
              />
            )}
          </Group>
        </Group>

        <Space h="md" />

        {/* 更新頻度 */}
        <ScheduleSelector value={schedule} onChange={setSchedule} />

        <ModelSelect />

        <Center className="mt-10">
          <Button
            size="md"
            type="submit"
            disabled={!form.values.name || !hasValidUrl || isSubmitting}
            loading={isSubmitting}
          >
            {t('create_v2.start_training')}
          </Button>
        </Center>
      </form>
    </section>
  );
};

export default Website;
